import { useMemo } from "react";
import type { ExpertSummary } from "../../api";

type ExpertIdentity = Pick<ExpertSummary, "id" | "color_token">;

/** Deterministic per-expert waveform. Seeded from `expert.id` (sum-of-charcodes
 *  → sin pattern). When `active`, bars pulse at full opacity with a staggered
 *  scaleY animation; otherwise they sit at 0.55 opacity with no animation.
 *  Matches design-handoff/project/components.jsx:VoiceSignature.
 */
export function VoiceSignature({
  expert,
  active = false,
  height = 14,
  bars = 18,
}: {
  expert: ExpertIdentity;
  active?: boolean;
  height?: number;
  bars?: number;
}) {
  const seed = useMemo(
    () =>
      expert.id.split("").reduce((a, c) => a + c.charCodeAt(0), 0),
    [expert.id],
  );
  const pat = useMemo(() => {
    const arr: number[] = [];
    for (let i = 0; i < bars; i++) {
      const v =
        (Math.sin(seed * 0.13 + i * 0.7) +
          Math.sin(seed * 0.31 + i * 1.3)) *
        0.5;
      arr.push(0.25 + Math.abs(v) * 0.7);
    }
    return arr;
  }, [seed, bars]);

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        height,
      }}
      aria-hidden="true"
    >
      {pat.map((v, i) => (
        <span
          key={i}
          style={{
            display: "block",
            width: 2,
            height: `${v * 100}%`,
            background: `var(--${expert.color_token})`,
            borderRadius: 1,
            opacity: active ? 1 : 0.55,
            animation: active
              ? `vs-pulse 1.2s ${i * 0.07}s ease-in-out infinite`
              : "none",
          }}
        />
      ))}
      <style>{`
        @keyframes vs-pulse {
          0%,100% { transform: scaleY(1); }
          50% { transform: scaleY(1.4); }
        }
      `}</style>
    </div>
  );
}
